import type { Params } from '@angular/router';

import { Injectable, inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { blockSchema, webNormalize } from '@web/store/page-structure/page-structure/mockStructureData';

import { SiteHttpService } from '@web/store/website/site/site.http.service';
import { isLegacyWidget } from '@common/helpers/widget-envelope';
import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ModalsFacade } from '@web/store/modals/modals.facade';
import { RouterFacade } from '@web/store/router/router.facade';

import NotifyActions from '@common/utils/notify/notify.actions';
import SectionsActions from '../sections/sections.actions';
import ColumnsActions from '../columns/columns.actions';
import GridsActions from '../grids/grids.actions';
import WidgetsLegacyActions from '../widgets/widgets.actions';
import TabsActions from '../tabs/tabs.actions';
import BlocksActions from './blocks.actions';

@Injectable()
export class BlocksEffects {
    private readonly actions$ = inject(Actions);
    private readonly routerFacade = inject(RouterFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly modalsFacade = inject(ModalsFacade);
    private readonly websiteHttpService = inject(SiteHttpService);
    private readonly translateService = inject(TranslateService);

    public readonly addBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.addBlock),
            switchMap(({ block, index }) => {
                const { blocks, columns, sections, widgets } = webNormalize(block, blockSchema);

                return [
                    BlocksActions.blockAdded({ block: blocks, index }),
                    SectionsActions.addManySections({ sections }),
                    ColumnsActions.addManyColumns({ columns }),
                    WidgetsLegacyActions.addManyWidgets({ widgets }),
                ];
            })
        )
    );

    public readonly createBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.createBlock),
            switchMap(({ index, widgetCorrelationData }) => {
                const newBlockInstance = this.blocksFacade.getBlockInstanceValue();
                const blockId = newBlockInstance.id;

                return [
                    BlocksActions.blockCreated({ block: newBlockInstance, index }),
                    SectionsActions.createSection({ index: 0, blockId, widgetCorrelationData, skipHistory: true }),
                ];
            })
        )
    );

    public readonly createSpecificBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.createSpecificBlock),
            switchMap(({ index }) => {
                const newBlockInstance = this.blocksFacade.getSpecificBlockInstanceValue();
                const blockId = newBlockInstance.id;
                return [
                    BlocksActions.blockCreated({ block: newBlockInstance, index }),
                    SectionsActions.createSpecificSection({ index: 0, blockId, skipHistory: true }),
                ];
            })
        )
    );

    public readonly createZeroBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.createZeroBlock),
            switchMap(({ index }) => {
                const newBlockInstance = this.blocksFacade.getZeroBlockInstanceValue();
                const blockId = newBlockInstance.id;
                return [
                    BlocksActions.blockCreated({ block: newBlockInstance, index }),
                    SectionsActions.createZeroSection({ index: 0, blockId, skipHistory: true }),
                ];
            })
        )
    );

    public readonly copyBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.copyBlock),
            switchMap(({ initialBlock, index }) => {
                // The custom id is a DOM id the published page renders, so a copy has to
                // drop it — otherwise two blocks answer to the same anchor. It moved
                // under `meta` when the block was migrated.
                const copiedBlockEntity = WebWidgetsFactoryService.recursiveEntityCopy({
                    ...initialBlock,
                    meta: { ...initialBlock.meta, displayId: null },
                });
                const {
                    blocks: copiedBlock,
                    columns,
                    sections,
                    widgets: copiedWidgets,
                } = webNormalize(copiedBlockEntity, blockSchema);

                const { widgets, grids, tabs } = copiedWidgets.reduce(
                    ({ widgets, grids, tabs }, item) => {
                        if (PageStructureService.isEntityTab(item)) {
                            tabs.push(item);
                        } else if (PageStructureService.isEntityGrid(item)) {
                            grids.push(item);
                        } else {
                            widgets.push(item);
                        }
                        return { widgets, grids, tabs };
                    },
                    { widgets: [], grids: [], tabs: [] }
                );

                return [
                    BlocksActions.blockCopied({ initialBlock, copiedBlock, copiedBlockEntity, index }),
                    SectionsActions.copyManySections({ sections }),
                    ColumnsActions.copyManyColumns({ columns }),
                    TabsActions.addMany({ entities: tabs }),
                    GridsActions.copyMany({
                        //TODO: !!!!!!
                        entities: grids.map((item) => {
                            const copy = {
                                ...item,
                            };

                            //TODO: !!!!!!
                            const tab = tabs.find((tab) => tab.children.includes(item.id));
                            if (tab) {
                                //TODO: !!!!!!
                                // copy['columnId'] = tab.parentId;
                                // copy['tabId'] = tab.id;
                                copy['parentId'] = tab.id;
                            } else {
                                const column = columns.find((col) => col.children?.includes(item.id));
                                if (column) {
                                    //TODO: !!!!!!
                                    copy['parentId'] = column.id;
                                }
                            }

                            return copy;
                        }),
                    }),
                    WidgetsLegacyActions.copyManyWidgets({
                        widgets: widgets.map((item) => {
                            const copy = { ...item };
                            const grid = grids.find((grid) => grid.children.some((row) => row.includes(item.id)));
                            if (grid) {
                                // Legacy keeps the grid in `structureId`; otherwise it is the widget's single parent link.
                                copy[isLegacyWidget(item) ? 'structureId' : 'parentId'] = grid.id;
                            }

                            return copy;
                        }),
                    }),
                ];
            })
        )
    );

    public readonly copyToPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BlocksActions.copyBlockToPage),
                withLatestFrom(this.routerFacade.routerParams$, ({ initialBlock }, routerParams: Params) => ({
                    initialBlock,
                    routerParams,
                })),
                map(({ initialBlock, routerParams }) => {
                    this.modalsFacade.showCopyBlockToPageModal(initialBlock, routerParams.siteId, routerParams.pageId);
                })
            ),
        { dispatch: false }
    );

    public readonly copyBlockToPageRequest$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BlocksActions.copyBlockToPageRequest),
                map(({ pageIds, block }) => ({
                    pageIds,
                    block: WebWidgetsFactoryService.recursiveEntityCopy({
                        ...block,
                        meta: { ...block.meta, displayId: null },
                    }),
                })),
                switchMap(({ pageIds, block }) => this.websiteHttpService.copyBlockToPage(pageIds, block)),
                switchMap(() => {
                    return [
                        BlocksActions.copyBlockToPageSuccess(),
                        NotifyActions.showNotify({
                            toastType: 'success',
                            message: this.translateService.instant('block_copy_to_page_success'),
                        }),
                    ];
                })
            ),
        { dispatch: true }
    );
}
